/**
 * Task Filters
 *
 * Shared task filtering helpers for Space and Folder views.
 *
 * Primary responsibilities:
 * - Normalize and format status values
 * - Apply text and advanced structured filters
 * - Group tasks by ordered status buckets
 *
 * Dependencies:
 * - None
 */

export type TaskFilterOperator =
  | "equals"
  | "not_equals"
  | "contains"
  | "not_contains"
  | "greater_than"
  | "less_than"
  | "greater_equal"
  | "less_equal"
  | "is_empty"
  | "is_not_empty"
  | "in"
  | "not_in"
  | "between"
  | "date_range"

export type TaskFilterCondition = {
  id: string
  field: string
  operator: TaskFilterOperator
  value: unknown
  logic?: "and" | "or"
}

export type TaskStatusGroup<T> = {
  status: string
  label: string
  tasks: T[]
}

// --------------

export const normalizeTaskStatus = (
  status: string | null | undefined
): string => {
  if (!status) return "todo"

  const normalized = status.toLowerCase().replace(/[-\s]/g, "_")

  switch (normalized) {
    case "done":
    case "completed":
    case "finished":
      return "done"
    case "in_progress":
    case "inprogress":
    case "doing":
    case "active":
      return "in_progress"
    case "blocked":
    case "stuck":
      return "blocked"
    case "cancelled":
    case "canceled":
    case "aborted":
      return "cancelled"
    default:
      // Preserve custom statuses from status templates
      return normalized
  }
}

// --------------

export const formatTaskStatusLabel = (
  status: string | null | undefined
): string => {
  const normalized = normalizeTaskStatus(status)

  if (normalized === "todo") return "To Do"
  if (normalized === "in_progress") return "In Progress"
  if (normalized === "done") return "Done"
  if (normalized === "blocked") return "Blocked"
  if (normalized === "cancelled") return "Cancelled"

  return normalized
    .split("_")
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

// --------------

const hasActiveFilterValue = (condition: TaskFilterCondition) => {
  if (condition.operator === "is_empty" || condition.operator === "is_not_empty") {
    return true
  }

  if (Array.isArray(condition.value)) {
    return condition.value.length > 0
  }

  return !(
    condition.value === undefined ||
    condition.value === null ||
    condition.value === ""
  )
}

const isValueEmpty = (value: unknown) => {
  if (Array.isArray(value)) return value.length === 0
  return value === undefined || value === null || value === ""
}

const toComparableValue = (value: unknown, field: string) => {
  if (typeof value === "string") {
    const normalizedValue = value.toLowerCase()
    if (field === "status") return normalizeTaskStatus(normalizedValue)
    return normalizedValue
  }
  return value
}

const compareAsNumber = (value: unknown) => {
  if (typeof value === "number") return value
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const compareAsDate = (value: unknown) => {
  if (typeof value !== "string" || !value) return null
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

const getConditionFieldValue = <T extends object>(
  item: T,
  field: string
) => {
  if (field.startsWith("custom_")) {
    const customFieldKey = field.slice("custom_".length)
    const customFields = (item as Record<string, unknown>).custom_fields

    if (
      customFields &&
      typeof customFields === "object" &&
      !Array.isArray(customFields)
    ) {
      return (customFields as Record<string, unknown>)[customFieldKey]
    }

    return undefined
  }

  return (item as Record<string, unknown>)[field]
}

const toConditionOptionValues = (value: unknown, field: string) => {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value
          .split(",")
          .map(part => part.trim())
          .filter(Boolean)
      : value === undefined || value === null || value === ""
        ? []
        : [value]

  return rawValues.map(option => toComparableValue(option, field))
}

const evaluateCondition = <T extends object>(
  item: T,
  condition: TaskFilterCondition
) => {
  const fieldValue = getConditionFieldValue(item, condition.field)
  const normalizedFieldValue = toComparableValue(fieldValue, condition.field)
  const normalizedConditionValue = toComparableValue(condition.value, condition.field)

  switch (condition.operator) {
    case "equals":
      return normalizedFieldValue === normalizedConditionValue
    case "not_equals":
      return normalizedFieldValue !== normalizedConditionValue
    case "contains": {
      if (Array.isArray(normalizedFieldValue)) {
        return normalizedFieldValue.some(value =>
          String(value).toLowerCase().includes(String(normalizedConditionValue))
        )
      }
      return String(normalizedFieldValue || "")
        .toLowerCase()
        .includes(String(normalizedConditionValue || "").toLowerCase())
    }
    case "not_contains": {
      if (Array.isArray(normalizedFieldValue)) {
        return !normalizedFieldValue.some(value =>
          String(value).toLowerCase().includes(String(normalizedConditionValue))
        )
      }
      return !String(normalizedFieldValue || "")
        .toLowerCase()
        .includes(String(normalizedConditionValue || "").toLowerCase())
    }
    case "greater_than": {
      const left = compareAsNumber(fieldValue) ?? compareAsDate(fieldValue)
      const right =
        compareAsNumber(condition.value) ?? compareAsDate(condition.value)
      return left !== null && right !== null ? left > right : false
    }
    case "less_than": {
      const left = compareAsNumber(fieldValue) ?? compareAsDate(fieldValue)
      const right =
        compareAsNumber(condition.value) ?? compareAsDate(condition.value)
      return left !== null && right !== null ? left < right : false
    }
    case "greater_equal": {
      const left = compareAsNumber(fieldValue) ?? compareAsDate(fieldValue)
      const right =
        compareAsNumber(condition.value) ?? compareAsDate(condition.value)
      return left !== null && right !== null ? left >= right : false
    }
    case "less_equal": {
      const left = compareAsNumber(fieldValue) ?? compareAsDate(fieldValue)
      const right =
        compareAsNumber(condition.value) ?? compareAsDate(condition.value)
      return left !== null && right !== null ? left <= right : false
    }
    case "is_empty":
      return isValueEmpty(fieldValue)
    case "is_not_empty":
      return !isValueEmpty(fieldValue)
    case "in": {
      const normalizedOptions = toConditionOptionValues(
        condition.value,
        condition.field
      )
      if (normalizedOptions.length === 0) return false

      if (Array.isArray(normalizedFieldValue)) {
        return normalizedFieldValue.some(value =>
          normalizedOptions.includes(toComparableValue(value, condition.field))
        )
      }

      return normalizedOptions.includes(normalizedFieldValue)
    }
    case "not_in": {
      const normalizedOptions = toConditionOptionValues(
        condition.value,
        condition.field
      )
      if (normalizedOptions.length === 0) return true

      if (Array.isArray(normalizedFieldValue)) {
        return !normalizedFieldValue.some(value =>
          normalizedOptions.includes(toComparableValue(value, condition.field))
        )
      }

      return !normalizedOptions.includes(normalizedFieldValue)
    }
    case "between": {
      if (!condition.value || typeof condition.value !== "object") return false
      const bounds = condition.value as { min?: unknown; max?: unknown }
      const current = compareAsNumber(fieldValue)
      const min = compareAsNumber(bounds.min)
      const max = compareAsNumber(bounds.max)
      if (current === null || min === null || max === null) return false
      return current >= min && current <= max
    }
    case "date_range": {
      if (!condition.value || typeof condition.value !== "object") return false
      const range = condition.value as { start?: unknown; end?: unknown }
      const current = compareAsDate(fieldValue)
      const start = compareAsDate(range.start)
      const end = compareAsDate(range.end)
      if (current === null || start === null || end === null) return false
      return current >= start && current <= end
    }
    default:
      return true
  }
}

// --------------

export const applyTaskFilters = <T extends object>(
  tasks: T[],
  conditions: TaskFilterCondition[]
): T[] => {
  const activeConditions = conditions.filter(hasActiveFilterValue)
  if (activeConditions.length === 0) return tasks

  return tasks.filter(task => {
    let result = true

    activeConditions.forEach((condition, index) => {
      const conditionResult = evaluateCondition(task, condition)

      if (index === 0) {
        result = conditionResult
        return
      }

      if (condition.logic === "or") {
        result = result || conditionResult
        return
      }

      result = result && conditionResult
    })

    return result
  })
}

// --------------

export const applyTextSearch = <T extends Record<string, unknown>>(
  items: T[],
  query: string,
  fields: Array<keyof T>
): T[] => {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return items

  return items.filter(item =>
    fields.some(field => {
      const value = item[field]

      if (Array.isArray(value)) {
        return value.some(entry =>
          String(entry).toLowerCase().includes(normalizedQuery)
        )
      }

      return String(value || "").toLowerCase().includes(normalizedQuery)
    })
  )
}

// --------------

export const groupTasksByStatus = <T>(
  tasks: T[],
  orderedStatuses: string[],
  getStatus: (task: T) => string | null | undefined,
  getLabel?: (status: string) => string
): TaskStatusGroup<T>[] => {
  const ordered = orderedStatuses.map(normalizeTaskStatus)
  const seen = new Set<string>()
  const orderedUnique = ordered.filter(status => {
    if (seen.has(status)) return false
    seen.add(status)
    return true
  })

  const grouped = new Map<string, T[]>()
  tasks.forEach(task => {
    const normalizedStatus = normalizeTaskStatus(getStatus(task))
    const current = grouped.get(normalizedStatus) || []
    current.push(task)
    grouped.set(normalizedStatus, current)
  })

  const unknownStatuses = [...grouped.keys()].filter(
    status => !orderedUnique.includes(status)
  )

  const outputOrder = [...orderedUnique, ...unknownStatuses]

  return outputOrder
    .map(status => ({
      status,
      label: getLabel ? getLabel(status) : formatTaskStatusLabel(status),
      tasks: grouped.get(status) || []
    }))
    .filter(group => group.tasks.length > 0)
}
